import { dateLib } from "@agentcall/core/lib"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs"
import { Suspense, useMemo } from "react"
import { Book, Home, MoreHorizontal } from "react-feather"
import { useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import type { z as zod } from "zod"
import * as z from "zod"
import { type TenantApplicationModels, tenantApplicationApi } from "@/api/client/tenant-application"
import type { TenantApplicationList200TenantApplicationsItemRoom } from "@/api/generated/models"
import { Button } from "@/components/ui/button"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner/Spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { openInNewTab } from "@/lib/navigation"

// 検索フォーム用のスキーマ
const searchFormSchema = z.object({
  id: z.number().optional(),
  propertyNameLike: z.string().optional(),
  roomNameLike: z.string().optional(),
  nameLike: z.string().optional(),
  nameKanaLike: z.string().optional(),
  startDate: z.date().optional(),
  ownerNameLike: z.string().optional(),
  approvalStatus: z.string().optional(),
})

type SearchFormValues = zod.infer<typeof searchFormSchema>

// nuqsのパーサー定義
const tenantApplicantsPageParsers = {
  page: parseAsInteger.withDefault(1),
  id: parseAsInteger,
  propertyNameLike: parseAsString,
  roomNameLike: parseAsString,
  nameLike: parseAsString,
  nameKanaLike: parseAsString,
  startDate: parseAsString,
  ownerNameLike: parseAsString,
  approvalStatus: parseAsString,
}

// 承認状況の定義
type ApprovalStatus = "pending" | "approved" | "rejected"

const APPROVAL_STATUS_LABELS: Record<ApprovalStatus, string> = {
  pending: "未承認",
  approved: "承認",
  rejected: "棄却",
}

const APPROVAL_STATUS_VARIANTS: Record<ApprovalStatus, "unapproved" | "approved" | "rejected"> = {
  pending: "unapproved",
  approved: "approved",
  rejected: "rejected",
}

// ApprovalStatusかどうかを判定するタイプガード
const isApprovalStatus = (status: string): status is ApprovalStatus => {
  return status in APPROVAL_STATUS_LABELS
}

// 安全にステータスラベルを取得するヘルパー
const getApprovalStatusLabel = (status: string): string => {
  return isApprovalStatus(status) ? APPROVAL_STATUS_LABELS[status] : "不明"
}

// 安全にステータスバリアントを取得するヘルパー
const getApprovalStatusVariant = (status: string): "unapproved" | "approved" | "rejected" => {
  return isApprovalStatus(status) ? APPROVAL_STATUS_VARIANTS[status] : "unapproved"
}

// DateをAPIフォーマット（YYYY-MM-DD）に変換するヘルパー
const formatDateForApi = (date: Date | undefined): string | null => {
  if (!date) return null
  return format(date, "yyyy-MM-dd")
}

const parseDate = (dateStr: string | null | undefined): Date | undefined => {
  if (!dateStr) return undefined
  const date = new Date(dateStr)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export function TenantApplicantsPage() {
  const itemsPerPage = 20
  const queryClient = useQueryClient()

  // URLクエリパラメーター管理
  const [queryParams, setQueryParams] = useQueryStates(tenantApplicantsPageParsers)

  // フォームの値をクエリから復元
  const searchParams = useMemo(() => {
    if (!queryParams) return null
    const hasSearchParams = Object.entries(queryParams)
      .filter(([key]) => key !== "page")
      .some(
        ([_, value]) =>
          value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : true)
      )

    if (!hasSearchParams) return null

    return {
      id: queryParams.id || undefined,
      propertyNameLike: queryParams.propertyNameLike || undefined,
      roomNameLike: queryParams.roomNameLike || undefined,
      nameLike: queryParams.nameLike || undefined,
      nameKanaLike: queryParams.nameKanaLike || undefined,
      startDate: parseDate(queryParams.startDate),
      ownerNameLike: queryParams.ownerNameLike || undefined,
      approvalStatus: queryParams.approvalStatus || undefined,
    } satisfies SearchFormValues
  }, [queryParams])

  const form = useForm<SearchFormValues>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      id: queryParams.id || undefined,
      propertyNameLike: queryParams.propertyNameLike || "",
      roomNameLike: queryParams.roomNameLike || "",
      nameLike: queryParams.nameLike || "",
      nameKanaLike: queryParams.nameKanaLike || "",
      startDate: parseDate(queryParams.startDate) || undefined,
      ownerNameLike: queryParams.ownerNameLike || "",
      approvalStatus: queryParams.approvalStatus || "",
    },
  })

  const onSubmit = (formData: SearchFormValues) => {
    // URLパラメーターを更新
    setQueryParams({
      page: 1, // 検索時はページを1にリセット
      id: formData.id || null,
      propertyNameLike: formData.propertyNameLike || null,
      roomNameLike: formData.roomNameLike || null,
      nameLike: formData.nameLike || null,
      nameKanaLike: formData.nameKanaLike || null,
      startDate: formatDateForApi(formData.startDate),
      ownerNameLike: formData.ownerNameLike || null,
      approvalStatus: formData.approvalStatus || null,
    })
    queryClient.invalidateQueries({ queryKey: ["/tenant_applications"] })
  }

  const onReset = () => {
    form.reset({
      id: undefined,
      propertyNameLike: "",
      roomNameLike: "",
      nameLike: "",
      nameKanaLike: "",
      startDate: undefined,
      ownerNameLike: "",
      approvalStatus: "",
    })
    // URLパラメーターをクリア
    setQueryParams({
      page: 1,
      id: null,
      propertyNameLike: null,
      roomNameLike: null,
      nameLike: null,
      nameKanaLike: null,
      startDate: null,
      ownerNameLike: null,
      approvalStatus: null,
    })
  }

  const handlePageChange = (page: number) => {
    setQueryParams({ page })
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">新規入居者申込一覧</h1>

      <div className="">
        <div className="p-4 border-b bg-white rounded-lg shadow-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">申請フォームID</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder=""
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? Number.parseInt(e.target.value, 10) : null
                            )
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="propertyNameLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">物件名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="roomNameLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">部屋番号</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="nameLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">契約申請者氏名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="nameKanaLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">契約申請者氏名（カナ）</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">賃貸借契約開始日</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="日付を選択"
                          className="h-[32px]"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ownerNameLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">オーナー名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="approvalStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">承認状況</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value || ""}
                          onChange={field.onChange}
                          options={[
                            { value: "pending", label: "未承認" },
                            { value: "approved", label: "承認" },
                            { value: "rejected", label: "棄却" },
                          ]}
                          placeholder="選択してください"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" variant="default" size="sm">
                  検索
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="font-bold border-primary"
                  size="sm"
                  onClick={onReset}
                >
                  検索をリセット
                </Button>
              </div>
            </form>
          </Form>
        </div>

        <ReactErrorBoundary>
          <Suspense fallback={<TenantApplicantsPageFallback />}>
            <TenantApplicantsPageContent
              searchParams={searchParams}
              currentPage={queryParams.page}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
            />
          </Suspense>
        </ReactErrorBoundary>
      </div>
    </div>
  )
}

interface TenantApplicantsPageContentProps {
  searchParams: SearchFormValues | null
  currentPage: number
  onPageChange: (page: number) => void
  itemsPerPage: number
}

function TenantApplicantsPageContent({
  searchParams,
  currentPage,
  onPageChange,
  itemsPerPage,
}: TenantApplicantsPageContentProps) {
  const handleDetail = (id: number) => {
    openInNewTab(`/tenant-applicants/${id}`)
  }

  const apiParams = useMemo(() => {
    const params: TenantApplicationModels.TenantApplicationListParams = {
      page: currentPage,
      limit: itemsPerPage,
    }

    type SearchableKeys = keyof TenantApplicationModels.TenantApplicationListParams
    const keys: readonly SearchableKeys[] = [
      "id",
      "propertyNameLike",
      "nameLike",
      "nameKanaLike",
      "roomNameLike",
      "ownerNameLike",
      "startDate",
      "approvalStatus",
    ] as const

    keys.forEach((key) => {
      if (searchParams?.[key as keyof SearchFormValues]) {
        params[key as keyof TenantApplicationModels.TenantApplicationListParams] = searchParams[
          key as keyof SearchFormValues
          // biome-ignore lint/suspicious/noExplicitAny: 暫定対応
        ] as any
      }
    })

    params.withRoom = "true"
    params.withRoomProperty = "true"
    params.withRoomPropertyOwner = "true"
    return params
  }, [searchParams, currentPage, itemsPerPage])

  // APIリクエスト
  const { data } = tenantApplicationApi.useTenantApplicationListSuspense(apiParams)

  // ページネーション計算
  const totalCount = data.total
  const totalPages = Math.ceil(totalCount / itemsPerPage)

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  const renderPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    const halfRange = Math.floor(maxVisiblePages / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={(e) => {
              e.preventDefault()
              handlePageChange(i)
            }}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      )
    }
    return pages
  }

  const renderPropertyName = (room?: TenantApplicationList200TenantApplicationsItemRoom) => {
    if (!room || !room.property) return "-"
    return room.property.name
  }

  const renderOwnerName = (room?: TenantApplicationList200TenantApplicationsItemRoom) => {
    if (!room || !room.property || !room.property.owner) return "-"
    return room.property.owner.name
  }

  return (
    <div className="mt-4">
      <div className="flex items-end justify-between mb-4">
        <div className="text-sm text-slate-400">{totalCount}件の申込が見つかりました</div>
      </div>

      <div className="bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap font-bold">申請フォームID</TableHead>
              <TableHead className="whitespace-nowrap font-bold">送信完了日時</TableHead>
              <TableHead className="whitespace-nowrap font-bold">物件名</TableHead>
              <TableHead className="whitespace-nowrap font-bold">部屋番号</TableHead>
              <TableHead className="whitespace-nowrap font-bold">契約申請者氏名</TableHead>
              <TableHead className="whitespace-nowrap font-bold">カナ</TableHead>
              <TableHead className="whitespace-nowrap font-bold">賃貸借契約開始日</TableHead>
              <TableHead className="whitespace-nowrap font-bold">オーナー名</TableHead>
              <TableHead className="whitespace-nowrap font-bold">承認状況</TableHead>
              <TableHead className="whitespace-nowrap font-bold">オーナー回答日</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.tenantApplications.map((application) => (
              <TableRow key={application.id}>
                <TableCell>
                  <Link
                    className="text-blue-600 cursor-pointer hover:underline"
                    to={`/tenant-applicants/${application.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {application.id}
                  </Link>
                </TableCell>
                <TableCell>
                  {application.submittedAt ? dateLib.formatDateISO(application.submittedAt) : "-"}
                </TableCell>
                <TableCell className="truncate">{renderPropertyName(application.room)}</TableCell>
                <TableCell>{application.room?.name || "-"}</TableCell>
                <TableCell>{application.name}</TableCell>
                <TableCell>{application.nameKana || "-"}</TableCell>
                <TableCell>{application.startDate}</TableCell>
                <TableCell>{renderOwnerName(application.room)}</TableCell>
                <TableCell>
                  <Label variant={getApprovalStatusVariant(application.approvalStatus)}>
                    {getApprovalStatusLabel(application.approvalStatus)}
                  </Label>
                </TableCell>
                <TableCell>
                  {application.ownerRespondedAt
                    ? dateLib.formatDateISO(application.ownerRespondedAt)
                    : "-"}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[200px]">
                      <DropdownMenuItem onClick={() => handleDetail(application.id)}>
                        <Book className="text-slate-600 mr-2" size={16} />
                        詳細
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          const propertyName = application.room?.property?.name || ""
                          const roomName = application.room?.name || ""
                          openInNewTab(
                            `/tickets?propertyName=${encodeURIComponent(propertyName)}&roomName=${encodeURIComponent(roomName)}`
                          )
                        }}
                      >
                        <Home className="text-slate-600 mr-2" size={16} />
                        案件を表示
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {data.total === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8">
                  申込が見つかりませんでした
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col">
          <div className="mt-4 flex items-center justify-between ml-auto">
            <div className="text-sm flex-shrink-0 text-primary mr-[30px]">
              {totalCount} 件中 {(currentPage - 1) * itemsPerPage + 1} -{" "}
              {Math.min(currentPage * itemsPerPage, totalCount)} 件を表示
            </div>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationFirst
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(1)
                    }}
                    disabled={currentPage === 1}
                    className="cursor-pointer"
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(currentPage - 1)
                    }}
                    disabled={currentPage === 1}
                    className="cursor-pointer"
                  />
                </PaginationItem>
                {renderPageNumbers()}
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(currentPage + 1)
                    }}
                    disabled={currentPage === totalPages}
                    className="cursor-pointer"
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLast
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(totalPages)
                    }}
                    disabled={currentPage === totalPages}
                    className="cursor-pointer"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}
    </div>
  )
}

function TenantApplicantsPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className={"text-slate-600 size-10 transition-opacity"} />
    </div>
  )
}
